import { ApiError, api } from './client'

/**
 * L'abonnement — [DECISIONS §157, §163, §165].
 *
 * **Aucun prix n'est écrit ici.** Ils viennent du serveur, formule par formule
 * et compte par compte : le prix d'entrée ne vaut qu'une fois, et une grille
 * recopiée côté écran afficherait le prix d'entrée à quelqu'un qui paiera le
 * tarif suivant. L'écart ne se verrait qu'au moment du paiement —
 * c'est-à-dire au pire moment.
 *
 * **Depuis [DECISIONS §211] le prix dépend aussi de la DATE** : une remise de
 * lancement court jusqu'au 22 novembre 2026, après quoi le prix rejoint le
 * tarif tout seul. Une grille en dur côté écran serait donc fausse à partir
 * d'un jour connu d'avance.
 */

/** `equipe` : un compte d'administration, qui ne connaît ni essai ni abonnement. */
export type EtatDAbonnement = 'aucun' | 'essai' | 'abonne' | 'expire' | 'equipe'

export interface FormuleOfferte {
  code: 'essai' | 'hebdo' | 'mensuel'
  libelle: string
  precision: string
  prix_gourdes: number
  /**
   * Le TARIF de la même formule, barré — §211. Nul quand il coïncide avec le
   * prix payé : un prix barré identique au prix payé est une publicité.
   *
   * Il est réel : c'est ce qui sera facturé dès la remise échue.
   */
  prix_habituel_gourdes: number | null
  achetable: boolean
}

export interface PaiementDuCandidat {
  id: string
  /** NOTRE référence — c'est elle qu'un candidat cite quand il écrit. */
  reference: string
  formule: string
  libelle: string
  montant_gourdes: number
  etat: 'en_attente' | 'reussi' | 'echoue' | 'annule' | 'abandonne'
  date: string
  credite_le: string | null
  /**
   * Rouvrir la page MonCash d'un paiement resté en attente plutôt que d'en
   * ouvrir un second : c'est le geste qui évite de payer deux fois.
   */
  redirect_url: string | null
}

export interface Abonnement {
  etat: EtatDAbonnement
  fin: string | null
  /**
   * En SECONDES, et non en jours : « il vous reste 41 h » est la phrase du
   * §157, et un compte en jours ne sait pas la dire.
   */
  secondes_restantes: number | null
  essai_deja_couru: boolean
  premier_abonnement: boolean
  formules: FormuleOfferte[]
  /**
   * Le dernier jour de la remise de lancement, `2026-11-22` — §211. Nul quand
   * elle est échue, puisqu'il n'y a alors plus rien de barré.
   *
   * **L'écran doit l'afficher.** Une remise dont l'échéance ne se voit pas est
   * une remise permanente déguisée, et le candidat qui la retrouve en février
   * apprend que le prix barré ne voulait rien dire.
   */
  remise_jusqu_au: string | null
  paiements: PaiementDuCandidat[]
}

export function abonnement(): Promise<{ data: Abonnement }> {
  return api<{ data: Abonnement }>('/me/abonnement')
}

export interface OuvertureDePaiement {
  id: string
  reference: string
  montant_gourdes: number
  formule: string
  redirect_url: string | null
}

/**
 * Ouvre un paiement MonCash.
 *
 * **Le montant n'est PAS envoyé.** Le serveur le recalcule à partir de la
 * formule et de l'historique du compte : l'écran n'a rien à dire sur le prix.
 */
export function payer(formule: 'hebdo' | 'mensuel'): Promise<{ data: OuvertureDePaiement }> {
  return api<{ data: OuvertureDePaiement }>('/me/abonnement/paiements', {
    method: 'POST',
    body: JSON.stringify({ formule }),
  })
}

/**
 * Cette erreur est-elle la fermeture de l'accès ? — [DECISIONS §157, §167].
 *
 * **402, et pas 403.** Le serveur distingue « confirme ton adresse » de « ton
 * accès est terminé » par le statut : sans cela, chaque écran devrait lire le
 * corps pour savoir laquelle des deux fermetures il vient de rencontrer. Le
 * `code` est vérifié en plus du statut, pour que la fonction ne se mette pas à
 * mentir le jour où une autre 402 apparaîtra.
 */
export function estAccesTermine(erreur: unknown): boolean {
  if (!(erreur instanceof ApiError) || erreur.status !== 402) return false

  return (
    typeof erreur.body === 'object' &&
    erreur.body !== null &&
    (erreur.body as { code?: unknown }).code === 'abonnement_expire'
  )
}

/**
 * Retire une tentative perdue de l'historique — [DECISIONS §305].
 *
 * **Le serveur ne supprime rien** : la ligne reste en base, c'est une pièce
 * comptable. Ce que ce geste change, c'est ce que le candidat voit — un
 * abonnement payé une fois n'a pas à laisser trois lignes à l'écran.
 *
 * Il refuse en `409` un paiement réussi ou encore en cours, et le message du
 * serveur dit lequel des deux : les recopier ici les ferait diverger.
 */
export function retirerLePaiement(id: string): Promise<unknown> {
  return api<unknown>('/me/abonnement/paiements/' + encodeURIComponent(id), {
    method: 'DELETE',
  })
}

/** Échange un coupon. Le serveur rend l'abonnement COMPLET après échange. */
export function echangerLeCoupon(code: string): Promise<{ data: Abonnement }> {
  return api<{ data: Abonnement }>('/me/abonnement/coupons', {
    method: 'POST',
    body: JSON.stringify({ code }),
  })
}

/**
 * « Il vous reste 41 h » — [DECISIONS §157].
 *
 * > « La montre se dit en une phrase — “il vous reste 41 h” — et ne s'optimise
 * > pas. »
 *
 * **En heures sous deux jours, en jours au-delà.** Dire « 29 jours » à qui en a
 * vingt-neuf est juste ; dire « 2 jours » à qui a quarante-et-une heures est
 * faux dans les deux sens — il en a plus d'un et moins de deux. C'est
 * précisément dans les dernières heures que le chiffre décide d'un achat, donc
 * c'est là qu'il doit être exact.
 *
 * **Exportée pour être testée seule.** Le compte à rebours est ce que l'écran
 * dit de plus important : le vérifier à travers un rendu complet rendrait
 * l'échec illisible.
 */
export function tempsRestant(secondes: number): string {
  if (secondes <= 0) return 'terminé'

  const heures = Math.floor(secondes / 3600)

  if (heures < 48) {
    if (heures < 1) {
      const minutes = Math.max(1, Math.floor(secondes / 60))

      return `${minutes} min`
    }

    return `${heures} h`
  }

  // `floor` et non `round` : annoncer 30 jours quand il en reste 29 et demi
  // ferait attendre un jour de trop celui qui compte sur la date.
  return `${Math.floor(heures / 24)} jours`
}

/**
 * Un montant en gourdes, tel qu'on l'écrit en Haïti.
 *
 * « HTG » APRÈS le nombre, séparé par une espace insécable étroite : le symbole
 * « G » seul se confond avec une lettre dans une phrase.
 *
 * **Le séparateur de milliers est normalisé, et il le faut.** `toLocaleString`
 * rend U+202F (espace fine insécable) ou U+00A0 (espace insécable) selon la
 * version d'ICU embarquée — deux caractères invisiblement différents, dont
 * dépendrait sinon toute comparaison de ce texte. On tranche ici, une fois.
 */
const FINE_INSECABLE = '\u202f'

export function gourdes(montant: number): string {
  const nombre = montant.toLocaleString('fr-FR').replace(/\s|\u00a0|\u202f/g, FINE_INSECABLE)

  return `${nombre}${FINE_INSECABLE}HTG`
}
